// charge.meta keys the backend reads at payment-settlement time.

// Set when this checkout is selling a membership enrollment — backend's
// MembershipService.enroll_from_charge_meta consumes this on successful
// payment to create the membership for the patient on the charge.
export const ENROLLMENT_MEMBERSHIP_META_KEY = "enrollmentMembershipId";

// Optional companion to ENROLLMENT_MEMBERSHIP_META_KEY: which provider gets
// credit for selling the membership at enrollment time.
export const ENROLLMENT_SOLD_BY_META_KEY = "enrollmentSoldByProviderId";

// Optional companion to ENROLLMENT_MEMBERSHIP_META_KEY: the recurring price in
// cents agreed with this patient, stamped on the member as their custom price.
// Needs memberships:enrollments:price; the backend refuses it otherwise.
export const ENROLLMENT_CUSTOM_PRICE_META_KEY =
  "enrollmentMembershipCustomPrice";

// The plan's terms as sold, read at settlement instead of the plan's current
// ones, so editing the plan before a Pay Later invoice is paid doesn't change
// what the patient bought. The same keys the online shop stamps.
export const ENROLLMENT_AUTO_RENEW_META_KEY = "enrollmentMembershipAutoRenew";
export const ENROLLMENT_BILLING_FREQUENCY_META_KEY =
  "enrollmentMembershipBillingFrequency";
export const ENROLLMENT_SETUP_FEE_META_KEY = "enrollmentMembershipSetupFee";
export const ENROLLMENT_FIRST_CYCLE_PRICE_META_KEY =
  "enrollmentMembershipFirstCyclePrice";

// When the patient starts receiving benefits (ISO). Absent = when payment lands.
export const ENROLLMENT_START_DATE_META_KEY = "enrollmentMembershipStartDate";

// Renewal anchor (ISO), sent only for a genuine off-cycle date or a deferred
// first cycle. Absent = start date + one billing cycle.
export const ENROLLMENT_NEXT_BILLING_DATE_META_KEY =
  "enrollmentMembershipNextBillingDate";

// Today's payment covers the setup fee only; the first cycle bills on the
// next billing date.
export const ENROLLMENT_DEFER_FIRST_CYCLE_META_KEY =
  "membershipDeferFirstCycle";

// The enrollment saved ahead so the patient could sign a form that prints its
// details. Payment activates that row instead of creating a new one.
export const ENROLLMENT_MEMBER_META_KEY = "enrollmentMemberId";

// Checkout-only: ticket lines staff took the new membership's benefit off,
// so it isn't applied to them again (e.g. a free facial saved for later).
export const ENROLLMENT_DECLINED_BENEFIT_LINES_META_KEY =
  "enrollmentMembershipDeclinedLineIds";

// Checkout-only: whether staff prorated the first payment. The price on the
// membership line already carries the proration; this restores the switch
// when the sale is reopened.
export const ENROLLMENT_PRORATE_META_KEY = "enrollmentMembershipProrate";

const ENROLLMENT_META_KEYS = [
  ENROLLMENT_MEMBERSHIP_META_KEY,
  ENROLLMENT_SOLD_BY_META_KEY,
  ENROLLMENT_CUSTOM_PRICE_META_KEY,
  ENROLLMENT_AUTO_RENEW_META_KEY,
  ENROLLMENT_BILLING_FREQUENCY_META_KEY,
  ENROLLMENT_SETUP_FEE_META_KEY,
  ENROLLMENT_FIRST_CYCLE_PRICE_META_KEY,
  ENROLLMENT_START_DATE_META_KEY,
  ENROLLMENT_NEXT_BILLING_DATE_META_KEY,
  ENROLLMENT_DEFER_FIRST_CYCLE_META_KEY,
  ENROLLMENT_PRORATE_META_KEY,
  ENROLLMENT_MEMBER_META_KEY,
  ENROLLMENT_DECLINED_BENEFIT_LINES_META_KEY,
];

// The enrollment keys a charge carries, in a fixed order, so two versions of
// a charge can be compared.
export function pickEnrollmentMeta(
  meta: Record<string, unknown> | undefined,
): Record<string, unknown> {
  return Object.fromEntries(
    ENROLLMENT_META_KEYS.filter((key) => meta?.[key] !== undefined).map(
      (key) => [key, meta?.[key]],
    ),
  );
}

// Returns a new meta object with the enrollment keys removed. Returns the
// original reference when nothing changed so callers can early-bail.
export function stripEnrollmentMeta(
  meta: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!meta) return meta;
  if (ENROLLMENT_META_KEYS.every((key) => meta[key] === undefined)) {
    return meta;
  }
  const next = { ...meta };
  for (const key of ENROLLMENT_META_KEYS) delete next[key];
  return next;
}
